import Swiper from "swiper";
import { Autoplay, Navigation, Pagination, A11y, EffectFade } from "swiper/modules";
import { initBulletPreview } from "./WowSliderBulletPreview";

export class WowSlider {
  private el: HTMLElement;
  private effectDelay: string | undefined;
  private effectDuration: string | undefined;
  private effectName: string | undefined;
  private logo: string | undefined;
  private isEditMode: boolean;
  private wrapper: HTMLElement | null;
  private swiper: Swiper | null;
  private captionEl: HTMLElement | null;

  constructor(el: HTMLElement) {
    this.el = el;

    this.effectDelay = this.el.dataset["wowSliderEffectDelay"];
    this.effectDuration = this.el.dataset["wowSliderEffectDuration"];
    this.effectName = this.el.dataset["wowSliderEffect"];
    this.logo = this.el.dataset["wowSliderLogo"];
    this.isEditMode = this.el.dataset["wowSliderIsEditmode"] === "true";
    this.wrapper = this.el.querySelector(".wowSliderWrapper");
    this.swiper = null;
    this.captionEl = null;
  }

  init(): void {
    if (this.isEditMode) {
      return;
    }
    this.initSwiper();
  }

  private initSwiper() {
    if (!this.wrapper) return;

    // Collect slides from server-rendered wow slider markup (.ws_images ul li)
    const listItems = Array.from(
      this.wrapper.querySelectorAll<HTMLElement>(".ws_images li")
    );
    if (!listItems.length) return;

    // Collect captions from each slide's image title attribute
    const captions = listItems.map(
      (li) => li.querySelector("img")?.getAttribute("title") || ""
    );

    // Build Swiper wrapper and move slide content into swiper-slide divs
    const swiperWrapper = document.createElement("div");
    swiperWrapper.className = "swiper-wrapper";

    listItems.forEach((li) => {
      const slide = document.createElement("div");
      slide.className = "swiper-slide";
      while (li.firstChild) {
        slide.appendChild(li.firstChild);
      }
      swiperWrapper.appendChild(slide);
    });

    // Replace .ws_images content with the Swiper structure
    const wsImages = this.wrapper.querySelector<HTMLElement>(".ws_images");
    if (!wsImages) return;
    wsImages.innerHTML = "";
    wsImages.appendChild(swiperWrapper);

    // Create navigation arrows (matching existing CSS selectors)
    const prevBtn = this.createNavButton("ws_prev", "Previous slide");
    this.wrapper.appendChild(prevBtn);

    const nextBtn = this.createNavButton("ws_next", "Next slide");
    this.wrapper.appendChild(nextBtn);

    // Use server-rendered pagination container if present (conditionally rendered by HTL)
    const bulletsContainer = this.wrapper.querySelector<HTMLElement>(".ws_bullets");
    const bulletsInner = bulletsContainer?.querySelector<HTMLElement>("div") ?? null;

    // Extract thumbnail images from HTL-rendered bullets before Swiper clears them
    const thumbImgs = bulletsInner
      ? Array.from(bulletsInner.querySelectorAll<HTMLImageElement>("a img"))
      : [];

    // Create caption element (matching existing CSS selector .ws-title)
    this.captionEl = document.createElement("div");
    this.captionEl.className = "ws-title";
    this.captionEl.style.display = "none";
    this.wrapper.appendChild(this.captionEl);

    const delay = parseInt(this.effectDelay ?? "") || 2000;
    const speed = parseInt(this.effectDuration ?? "") || 1000;
    const isFade = this.effectName === "fade";

    const modules = [Autoplay, Navigation, A11y];
    if (bulletsInner) modules.push(Pagination);
    if (isFade) modules.push(EffectFade);

    this.swiper = new Swiper(wsImages, {
      modules,
      loop: true,
      speed,
      allowTouchMove: true,
      autoplay: {
        delay,
        disableOnInteraction: false,
      },
      slidesPerView: 1,
      ...(isFade
        ? { effect: "fade", fadeEffect: { crossFade: true } }
        : {}),
      ...(bulletsInner
        ? {
          pagination: {
            el: bulletsInner,
            type: "bullets",
            clickable: true,
            // Render <a> elements so existing .ws_bullets a CSS rules apply
            renderBullet: (_index, className) =>
              `<a class="${className}" role="button"></a>`,
            bulletClass: "ws-slide-bullet",
            bulletActiveClass: "ws_selbull",
          },
        }
        : {}),
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      a11y: { enabled: true },
      on: {
        afterInit: (swiper) => {
          this.updateCaption(captions[swiper.realIndex]);
        },
        slideChange: (swiper) => {
          this.updateCaption(captions[swiper.realIndex]);
        },
      },
    });

    if (bulletsInner && thumbImgs.length) {
      initBulletPreview(bulletsInner, thumbImgs);
    }
  }

  private createNavButton(className: string, ariaLabel: string): HTMLAnchorElement {
    const btn = document.createElement("a");
    btn.href = "#";
    btn.className = className;
    btn.setAttribute("role", "button");
    btn.setAttribute("aria-label", ariaLabel);
    btn.addEventListener("click", (e) => e.preventDefault());
    return btn;
  }

  private updateCaption(caption: string) {
    if (!this.captionEl) return;
    if (caption) {
      this.captionEl.textContent = caption;
      this.captionEl.style.display = "";
    } else {
      this.captionEl.style.display = "none";
    }
  }

  destroy(): void {
    this.swiper?.destroy();
    this.swiper = null;
  }


  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="wow-slider"]')
      .forEach((el) => {
        new WowSlider(el).init();
      });
  }
}